import './App.css';
import { ReactElement, useEffect, useState } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import {
  exportLiterals,
  exportUserSds,
  fetchChange,
  getPdfFileByUrl,
} from 'api/substances';
import { CACHE_NAME, LITERAL_CACHE_KEY, SDS_CACHE_KEY } from 'api';
import {
  validateToken,
  cacheFile,
  putCache,
  formatISODate,
  renderSnackbar,
  removeCache,
  isIOS,
  isInStandaloneMode,
  getCachedChunkSdsData,
} from 'utils';
import AppThemeProvider from 'utils/AppThemeProvider';
import { defaultLanguage, getI18n } from 'utils/i18n';
import { ExportUserSDSDto, FetchChangeDto, LiteralInterface } from 'interfaces';
import { ApiResponse, ErrorApiPayload } from 'interfaces/api';
import APP_PAGE from 'enums/page.enums';
import ERROR_API_CODE from 'enums/api-error-code';
/* Components */
import { SnackbarProvider, enqueueSnackbar, closeSnackbar } from 'notistack';
import GlobalSearchSDS from 'pages/global-search-sds';
import MySDS from 'pages/my-sds';
import NoAccess from 'pages/no-access';
import InstallationPopup from 'components/popup/installation-popup';

const App = () => {
  const i18n = getI18n({ lng: defaultLanguage });
  const { t } = useTranslation();
  const [access, setAccess] = useState<boolean>(true);
  const [literal, setLiteral] = useState<LiteralInterface | null>(null);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [installation, setInstallation] = useState<any>(null);
  const [page, setPage] = useState<APP_PAGE>(APP_PAGE.MY_SDS);
  const [newServiceWorker, setNewServiceWorker] =
    useState<ServiceWorkerRegistration | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [installationPopup, setInstallationPopup] =
    useState<ReactElement | null>(null);
  const [defaultGlobalSearch, setDefaultGlobalSearch] = useState<string>('');
  const [loadResource, setLoadResource] = useState<boolean>(false);
  const [status, setStatus] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<boolean>(true);

  const clearData = async (): Promise<void> => {
    setLiteral(null);
    localStorage.removeItem('sync_sds');
    localStorage.removeItem('last_updated');
    await removeCache();
  };

  const fetchAndDownloadPdfs = async (
    data: ExportUserSDSDto,
  ): Promise<void> => {
    setStatus(`${t('common:downloading_pdf_files')}...`);
    const cache = await caches.open(CACHE_NAME);
    for (const sdsId in data) {
      const pdfUrl = data[sdsId].public_view_url;
      if (pdfUrl) {
        const cachedResponse = await cache.match(pdfUrl);
        if (cachedResponse) continue;
        const downloadResponse = await getPdfFileByUrl(pdfUrl);
        if (downloadResponse.status === 200)
          cacheFile(pdfUrl, downloadResponse.data, 'application/pdf');
      }
    }
    setStatus(null);
  };

  const fetchAndDownloadIcon = async (
    data: ExportUserSDSDto,
  ): Promise<void> => {
    const cache = await caches.open(CACHE_NAME);
    for (const sdsId in data) {
      const iconList = data[sdsId].icons;
      if (iconList) {
        for (let iconItem of iconList) {
          try {
            const cachedResponse = await cache.match(iconItem.url);
            if (cachedResponse) continue;
            await fetch(iconItem.url, {
              method: 'GET',
              mode: 'no-cors',
            });
          } catch (err) {}
        }
      }
    }
  };

  const fetchAndDownloadResource = async (): Promise<void> => {
    const cacheData = await getCachedChunkSdsData();
    fetchAndDownloadPdfs(cacheData);
    fetchAndDownloadIcon(cacheData);
  };

  const fetchChangeData = (
    updateLatest?: boolean,
    fetchSds: boolean = false,
  ): void => {
    const syncSds = localStorage.getItem('sync_sds');
    if (syncSds && syncSds.split(',').length > 0) {
      setStatus(`${t('common:loading_data')}...`);
      renderSnackbar(`${t('common:offline_data_is_synced')}...`);
      if (!syncing || fetchSds) fetchUserSds();
      return;
    }

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      renderSnackbar(t('common:no_internet_connection'));
      setSyncing(false);
      setStatus(null);
      return;
    }

    const fromDate = updateLatest
      ? localStorage.getItem('last_updated')
      : undefined;
    setStatus(
      fromDate
        ? `${t('common:loading_latest_changes')}...`
        : `${t('common:loading_data')}...`,
    );
    setSyncing(true);
    fetchChange(fromDate)
      .then(async (response: ApiResponse<FetchChangeDto | ErrorApiPayload>) => {
        switch (response.status) {
          case 200:
            if (!navigator.serviceWorker.controller) return;

            const changeData = response.data as FetchChangeDto;
            const updatedData = changeData.updated;
            const removedData = changeData.removed;
            const currentTime = new Date().toISOString();

            if (updatedData.length === 0 && removedData.length === 0) {
              renderSnackbar(t('common:no_changed_data'));
              setLoadResource(true);
              localStorage.setItem('last_updated', currentTime);
              setLastUpdated(formatISODate(currentTime));
              return;
            }

            if (updatedData.length > 0) {
              const syncSds = updatedData.join(',');
              localStorage.setItem('sync_sds', syncSds);
            }

            if (removedData.length > 0) {
              navigator.serviceWorker.controller.postMessage({
                type: 'REMOVE_SDS_CACHE',
                url: SDS_CACHE_KEY,
                data: removedData,
              });
            } else fetchUserSds();

            localStorage.setItem('last_updated', currentTime);
            setLastUpdated(formatISODate(currentTime));
            return;
          case 408:
            renderSnackbar(t('common:no_internet_connection'));
            setSyncing(false);
            setStatus(null);
            return;
          case 403:
            if (
              (response.data as ErrorApiPayload).error_code ===
              ERROR_API_CODE.OFFLINE_APP_ACCESS_DISABLED
            ) {
              setAccess(false);
              clearData();
              renderSnackbar(t('common:offline_app_access_disabled_by_admin'));
            } else if (
              (response.data as ErrorApiPayload).error_code ===
              ERROR_API_CODE.DEFAULT_NOT_PERMISSION
            )
              renderSnackbar(t('common:unauthorized_please_scan_qr_to_login'));
            else renderSnackbar(t('common:failed_to_fetch_data'));
            setLoadResource(true);
            return;
          default:
            renderSnackbar(t('common:failed_to_fetch_data'));
            setLoadResource(true);
            return;
        }
      })
      .catch(() => setLoadResource(true));
  };

  const fetchUserSdsListener = (event: MessageEvent): void => {
    if (event.data.type === 'UPDATE_SDS_CACHE_RESPONSE') {
      const syncSds = localStorage.getItem('sync_sds');
      const updatedIds: Array<string> = event.data.updated;
      if (
        updatedIds &&
        updatedIds.length > 0 &&
        !!syncSds &&
        syncSds.split(',').length > 0
      ) {
        const needUpdatedIds: Array<number> = [];
        for (let id of syncSds.split(',')) needUpdatedIds.push(Number(id));
        for (let id of updatedIds) {
          const index = needUpdatedIds.indexOf(Number(id));
          if (index !== -1) needUpdatedIds.splice(index, 1);
        }
        setStatus(
          `${t('common:download_remaining')}... ${needUpdatedIds.length}`,
        );
        localStorage.setItem('sync_sds', needUpdatedIds.join(','));
      }

      fetchUserSds();
    }
  };

  const fetchUserSds = (): void => {
    const syncSds = localStorage.getItem('sync_sds');
    if (!syncSds || syncSds.split(',').length === 0) {
      setLoadResource(true);
      setSyncing(false);
      localStorage.removeItem('sync_sds');
      renderSnackbar(t('common:offline_data_updated'));
      return;
    }

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      renderSnackbar(t('common:no_internet_connection'));
      setSyncing(false);
      setStatus(null);
      return;
    }

    setSyncing(true);
    const substanceIds: Array<number> = [];
    for (let id of syncSds.split(',')) substanceIds.push(Number(id));
    setStatus(`${t('common:download_remaining')}... ${substanceIds.length}`);
    const slicedSubstanceIds: Array<number> = substanceIds.splice(0, 50);
    exportUserSds(slicedSubstanceIds)
      .then(
        async (response: ApiResponse<ExportUserSDSDto | ErrorApiPayload>) => {
          switch (response.status) {
            case 200:
              if (!navigator.serviceWorker.controller) return;
              navigator.serviceWorker.controller.postMessage({
                type: 'PUT_SDS_CACHE',
                url: SDS_CACHE_KEY,
                data: response.data,
              });
              return;
            case 408:
              renderSnackbar(t('common:no_internet_connection'));
              setSyncing(false);
              setStatus(null);
              return;
            case 403:
              if (
                (response.data as ErrorApiPayload).error_code ===
                ERROR_API_CODE.OFFLINE_APP_ACCESS_DISABLED
              ) {
                setAccess(false);
                clearData();
                renderSnackbar(
                  t('common:offline_app_access_disabled_by_admin'),
                );
              } else if (
                (response.data as ErrorApiPayload).error_code ===
                ERROR_API_CODE.DEFAULT_NOT_PERMISSION
              )
                renderSnackbar(
                  t('common:unauthorized_please_scan_qr_to_login'),
                );
              else renderSnackbar(t('common:failed_to_fetch_data'));
              setLoadResource(true);
              return;
            default:
              renderSnackbar(t('common:failed_to_fetch_data'));
              setLoadResource(true);
              return;
          }
        },
      )
      .catch(() => setLoadResource(true));
  };

  const fetchLiterals = async (
    useCache: boolean = true,
    updateCallback?: (data: LiteralInterface) => void,
  ): Promise<void> => {
    if (useCache) {
      const cache = await caches.open(CACHE_NAME);
      const cachedResponse = await cache.match(LITERAL_CACHE_KEY);
      if (cachedResponse) {
        const data = await cachedResponse.json();
        i18n.changeLanguage(data.language ?? 'en');
        setLiteral(data);
        return;
      }
    }

    exportLiterals().then(
      (response: ApiResponse<LiteralInterface | ErrorApiPayload>) => {
        switch (response.status) {
          case 200:
            const data = response.data as LiteralInterface;
            if (!updateCallback) {
              i18n.changeLanguage(data.language ?? 'en');
              setLiteral(data);
              putCache(LITERAL_CACHE_KEY, data);
            } else updateCallback(data);
            return;
          case 403:
            if (
              (response.data as ErrorApiPayload).error_code ===
              ERROR_API_CODE.OFFLINE_APP_ACCESS_DISABLED
            )
              setLiteral(null);
            return;
          default:
            return;
        }
      },
    );
  };

  const renewData = async (): Promise<void> => {
    localStorage.removeItem('sync_sds');
    localStorage.removeItem('last_updated');
    await removeCache();
    window.location.reload();
  };

  /* Init service worker listener */
  useEffect(() => {
    navigator.serviceWorker.addEventListener('message', fetchUserSdsListener);

    const handleBeforeInstallPrompt = (event: any) => {
      event.preventDefault();
      setInstallation(event);
    };

    const onServiceWorkerUpdate = (event: {
      detail: ServiceWorkerRegistration;
    }) => setNewServiceWorker(event.detail);

    if (isIOS() && !isInStandaloneMode()) {
      setInstallationPopup(
        <InstallationPopup onClose={() => setInstallationPopup(null)} />,
      );
    }
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('swUpdateAvailable', onServiceWorkerUpdate);

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt,
      );
      window.removeEventListener('swUpdateAvailable', onServiceWorkerUpdate);
      navigator.serviceWorker.removeEventListener(
        'message',
        fetchUserSdsListener,
      );
    };
  }, []);

  useEffect(() => {
    let token = '';
    const urlParams = new URLSearchParams(window.location?.search);
    const tokenParam = urlParams.get('token');
    const localStorageToken = localStorage.getItem('access_token');

    let cookieToken = '';
    if (document.cookie) {
      cookieToken =
        document.cookie
          .split('; ')
          .find(row => row.startsWith('access_token='))
          ?.split('=')[1] || '';
    }

    if (tokenParam && validateToken(tokenParam)) token = tokenParam;

    if (!token && localStorageToken && validateToken(localStorageToken)) {
      token = localStorageToken;
      window.history.pushState(
        {},
        '',
        `${window.location.pathname}?token=${token}`,
      );
    }

    if (!token && cookieToken && validateToken(cookieToken)) {
      token = cookieToken;
      window.history.pushState(
        {},
        '',
        `${window.location.pathname}?token=${cookieToken}`,
      );
    }

    let renewData = false;
    if (token) {
      if (
        (localStorageToken && token !== localStorageToken) ||
        (cookieToken && token !== cookieToken)
      ) {
        renewData = true;
        clearData();
      }
      localStorage.setItem('access_token', token);
      document.cookie = `access_token=${token}; path=/; max-age=31536000`;
    }

    const lastUpdate = localStorage.getItem('last_updated');
    if (lastUpdate) setLastUpdated(formatISODate(lastUpdate));

    if (navigator.serviceWorker.controller) {
      fetchChangeData(true, true);
      fetchLiterals(!renewData);
    }
  }, []);

  useEffect(() => {
    if (loadResource) {
      setLoadResource(false);
      setSyncing(false);
      fetchAndDownloadResource();
    }
  }, [loadResource]);

  useEffect(() => {
    if (installation && initialLoad) {
      setInitialLoad(false);
      setInstallationPopup(
        <InstallationPopup
          onClose={() => setInstallationPopup(null)}
          onInstall={() => {
            installation.prompt();
            installation.userChoice.then((choiceResult: any) =>
              setInstallationPopup(null),
            );
          }}
        />,
      );
    }
  }, [installation]);

  useEffect(() => {
    if (newServiceWorker && newServiceWorker.waiting) {
      enqueueSnackbar(t('common:new_version_available'), {
        persist: true,
        action: key => (
          <div style={{ display: 'flex', gap: '10px' }}>
            <p
              className="snackbar-action-btn"
              onClick={() => closeSnackbar(key)}
            >
              {t('common:remind_me_later')}
            </p>
            <p
              className="snackbar-action-btn"
              onClick={() => {
                if (newServiceWorker && newServiceWorker.waiting) {
                  newServiceWorker.waiting.postMessage({
                    type: 'SKIP_WAITING',
                  });
                  closeSnackbar(key);
                }
              }}
            >
              {t('common:upgrade')}
            </p>
          </div>
        ),
      });
    }
  }, [newServiceWorker]);

  return (
    <AppThemeProvider>
      <I18nextProvider i18n={i18n}>
        <SnackbarProvider maxSnack={3}>
          {access ? (
            <>
              <div
                style={{ display: page === APP_PAGE.MY_SDS ? 'block' : 'none' }}
              >
                <MySDS
                  lastUpdated={lastUpdated}
                  literal={literal}
                  status={status}
                  onRefresh={() => fetchChangeData(true)}
                  onRenew={() => renewData()}
                  syncing={syncing}
                  onSwitchPage={(name: APP_PAGE, defaultSearch?: string) => {
                    if (defaultSearch) setDefaultGlobalSearch(defaultSearch);
                    setPage(name);
                  }}
                />
              </div>
              <div
                style={{
                  display:
                    page === APP_PAGE.GLOBAL_SEARCH_SDS ? 'block' : 'none',
                }}
              >
                <GlobalSearchSDS
                  literal={literal}
                  defaultSearch={defaultGlobalSearch}
                  onSwitchPage={(name: APP_PAGE) => setPage(name)}
                />
              </div>
            </>
          ) : (
            <NoAccess />
          )}
          {installationPopup}
        </SnackbarProvider>
      </I18nextProvider>
    </AppThemeProvider>
  );
};

export default App;
